import type { RewardCategory, RewardTier, RewardTransaction } from '../../hooks/useRewards';
import type { PaymentLogEntry } from '../../services/attribution-bridge';

export type WalletSettlementInput = PaymentLogEntry;

export type WalletActivityLane = 'rewards_points' | 'stablecoin_settlement';

export interface WalletRewardsInput {
  totalPoints: number;
  tier: RewardTier;
  categoryBreakdown: Record<string, number>;
  transactions: readonly RewardTransaction[];
}

export interface WalletReadModelInput {
  now?: number;
  walletAddress: string | null | undefined;
  rewards: WalletRewardsInput;
  settlements: readonly WalletSettlementInput[];
}

export interface WalletIdentityModel {
  state: 'anonymous' | 'authenticated';
  address?: string;
  shortAddress?: string;
}

export interface WalletRewardCategoryRow {
  category: RewardCategory | string;
  points: number;
}

export interface WalletPeriodTotals {
  currentWeek: number;
  currentMonth: number;
  allTime: number;
}

export interface WalletRewardsModel {
  visible: boolean;
  unit: 'points';
  totalPoints: number;
  tier: RewardTier;
  periodTotals: WalletPeriodTotals;
  categoryRows: WalletRewardCategoryRow[];
  transactions: readonly RewardTransaction[];
}

export interface WalletSettlementRow {
  id: string;
  txHash: string;
  txHashShort: string;
  campaignRefShort: string;
  recipientShort: string;
  amountAtomicUsdc: number;
  observedAt: number;
}

export interface WalletSettlementsModel {
  visible: boolean;
  unit: 'usdc';
  count: number;
  totalAtomicUsdc: number;
  rows: WalletSettlementRow[];
}

export interface WalletActivityRow {
  id: string;
  lane: WalletActivityLane;
  timestamp: number;
  title: string;
  detail: string;
}

export interface WalletDeferredCard {
  id: 'own-token-balance' | 'cashout' | 'full-earnings';
  title: string;
  status: 'deferred';
  reason: string;
}

export interface WalletReadModel {
  identity: WalletIdentityModel;
  rewards: WalletRewardsModel;
  settlements: WalletSettlementsModel;
  activity: WalletActivityRow[];
  deferredCards: WalletDeferredCard[];
}

const EMPTY_PERIOD_TOTALS: WalletPeriodTotals = {
  currentWeek: 0,
  currentMonth: 0,
  allTime: 0,
};

const DEFERRED_CARDS: WalletDeferredCard[] = [
  {
    id: 'own-token-balance',
    title: 'OwnYou token balance',
    status: 'deferred',
    reason: 'Deferred until the Producer Wallet and tokenomics model define backed OwnYou-token accounting.',
  },
  {
    id: 'cashout',
    title: 'Cash out',
    status: 'deferred',
    reason: 'Payout controls stay disabled until the settlement and payout workflow exists.',
  },
  {
    id: 'full-earnings',
    title: 'Full earnings',
    status: 'deferred',
    reason: 'Detailed earnings views move to the Producer Wallet work after Slice 06.',
  },
];

export function buildWalletReadModel(input: WalletReadModelInput): WalletReadModel {
  const walletAddress = normalizeWalletAddress(input.walletAddress);
  const identity = buildIdentity(walletAddress);
  const authenticated = identity.state === 'authenticated';
  const now = input.now ?? Date.now();

  const rewardTransactions = authenticated ? [...input.rewards.transactions] : [];
  const settlementRows = authenticated ? input.settlements.map(toSettlementRow) : [];

  const rewards: WalletRewardsModel = {
    visible: authenticated,
    unit: 'points',
    totalPoints: authenticated ? input.rewards.totalPoints : 0,
    tier: authenticated ? input.rewards.tier : 'bronze',
    periodTotals: authenticated
      ? calculateRewardPeriodTotals(rewardTransactions, now)
      : EMPTY_PERIOD_TOTALS,
    categoryRows: authenticated ? toCategoryRows(input.rewards.categoryBreakdown) : [],
    transactions: rewardTransactions,
  };

  const settlements: WalletSettlementsModel = {
    visible: authenticated,
    unit: 'usdc',
    count: settlementRows.length,
    totalAtomicUsdc: settlementRows.reduce((sum, row) => sum + row.amountAtomicUsdc, 0),
    rows: settlementRows,
  };

  return {
    identity,
    rewards,
    settlements,
    activity: authenticated
      ? buildActivityRows(rewardTransactions, settlementRows)
      : [],
    deferredCards: DEFERRED_CARDS,
  };
}

function normalizeWalletAddress(address: string | null | undefined): string | null {
  const normalized = address?.trim();
  return normalized ? normalized : null;
}

function buildIdentity(address: string | null): WalletIdentityModel {
  if (!address) {
    return { state: 'anonymous' };
  }

  return {
    state: 'authenticated',
    address,
    shortAddress: shortenAddress(address),
  };
}

function shortenAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function toCategoryRows(categoryBreakdown: Record<string, number>): WalletRewardCategoryRow[] {
  return Object.entries(categoryBreakdown)
    .filter(([, points]) => points > 0)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([category, points]) => ({ category, points }));
}

function calculateRewardPeriodTotals(
  transactions: readonly RewardTransaction[],
  now: number,
): WalletPeriodTotals {
  const weekStart = startOfUtcWeek(now);
  const monthStart = startOfUtcMonth(now);

  return transactions.reduce<WalletPeriodTotals>(
    (totals, transaction) => {
      const points = Number.isFinite(transaction.points) ? transaction.points : 0;
      totals.allTime += points;
      if (transaction.timestamp >= weekStart) {
        totals.currentWeek += points;
      }
      if (transaction.timestamp >= monthStart) {
        totals.currentMonth += points;
      }
      return totals;
    },
    { ...EMPTY_PERIOD_TOTALS },
  );
}

function startOfUtcWeek(timestamp: number): number {
  const date = new Date(timestamp);
  const day = date.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + mondayOffset,
  );
  return start;
}

function startOfUtcMonth(timestamp: number): number {
  const date = new Date(timestamp);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
}

function toSettlementRow(entry: WalletSettlementInput): WalletSettlementRow {
  const observedAt = normalizeTimestamp(entry.observed_at);
  return {
    id: `${entry.campaign_ref}:${entry.rotation_counter}:${entry.tx_hash}`,
    txHash: entry.tx_hash,
    txHashShort: shortenHash(entry.tx_hash),
    campaignRefShort: shortenHash(entry.campaign_ref),
    recipientShort: shortenHash(entry.recipient),
    amountAtomicUsdc: parseAtomicUsdc(entry.amount),
    observedAt: Number.isFinite(observedAt) ? observedAt : 0,
  };
}

function normalizeTimestamp(timestamp: number | string): number {
  if (typeof timestamp === 'number') return Number.isFinite(timestamp) ? timestamp : 0;
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseAtomicUsdc(amount: string): number {
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : 0;
}

function shortenHash(hash: string): string {
  if (hash.length <= 12) return hash;
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

function buildActivityRows(
  rewardTransactions: readonly RewardTransaction[],
  settlementRows: readonly WalletSettlementRow[],
): WalletActivityRow[] {
  const rewardRows = rewardTransactions.map<WalletActivityRow>((transaction) => ({
    id: `reward:${transaction.id}`,
    lane: 'rewards_points',
    timestamp: transaction.timestamp,
    title: `Reward +${transaction.points} points`,
    detail: rewardTransactionDetail(transaction),
  }));

  const settlementActivityRows = settlementRows.map<WalletActivityRow>((row) => ({
    id: `settlement:${row.id}`,
    lane: 'stablecoin_settlement',
    timestamp: row.observedAt,
    title: `Settlement ${formatAtomicUsdc(row.amountAtomicUsdc)} USDC`,
    detail: `${row.campaignRefShort} · ${row.txHashShort}`,
  }));

  return [...rewardRows, ...settlementActivityRows].sort((left, right) => {
    if (right.timestamp !== left.timestamp) return right.timestamp - left.timestamp;
    return left.id.localeCompare(right.id);
  });
}

function rewardTransactionDetail(transaction: RewardTransaction): string {
  if (transaction.missionTitle) return transaction.missionTitle;
  if (transaction.description) return transaction.description;
  return typeof transaction.type === 'string'
    ? transaction.type.replace(/_/g, ' ')
    : 'Reward points';
}

export function formatAtomicUsdc(amountAtomicUsdc: number): string {
  return (amountAtomicUsdc / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}
